/**
 * Recap engine — a "story so far" screen shown at the start of every run (fresh
 * or resumed). NO AI: it stitches a narrative from (a) the authored codex prose
 * in memories.js, (b) the player's save stats, and (c) a diff against a snapshot
 * taken at the LAST recap ("since you were last here…"). A rotating seed +
 * template pools + data-driven branches keep it from feeling repetitive.
 *
 * buildRecap() is pure: it takes the save data + a seed, returns the rendered
 * sections/facts AND a fresh `snapshot` the caller should persist for next time.
 */
import { makeRng } from "../core/util.js";
import { CODICES, CODEX_COUNT, TOTAL_MEMORIES, completedCodexCount, totalCollected, codexComplete, getMemory } from "../config/memories.js";
import { allEarned } from "../config/achievements.js";

// tier names in order, derived from the library (Foundation → Restoration)
const TIER_NAMES = (() => { const seen = []; for (const c of CODICES) if (!seen[c.tier - 1]) seen[c.tier - 1] = c.tierName; return seen; })();

// noun phrases so they read after "you are …" and "— …"
function notorietyTier(n) {
  return n > 800 ? "a name the Vault knows"
    : n > 400 ? "a face the Vault remembers"
    : n > 150 ? "a shadow the Vault watches"
    : n > 50 ? "a stranger the Vault has noticed"
    : "an unremembered seeker";
}

export function buildRecap({ character, profile, meta, prevSnap, seed }) {
  const rng = makeRng((seed >>> 0) || 1);
  const rpick = (arr) => arr[Math.floor(rng() * arr.length)];
  const shuffle = (arr) => { const a = arr.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };

  const collected = (meta && meta.codex) || {};
  const heroName = (profile && profile.name) || character.name;
  const kills = Math.floor((profile && profile.kills) || 0);
  const depth = Math.floor((profile && profile.best) || 0);
  const bosses = Math.floor((profile && profile.bosses) || 0);
  const runs = Math.floor((profile && profile.runs) || 0);
  const level = (profile && profile.level) || 1;
  const memCollected = totalCollected(collected);
  const codexDone = completedCodexCount(collected);
  const notoriety = (meta && meta.notoriety) || 0;
  const insight = Math.floor((meta && meta.insight) || 0);
  const copper = Math.floor((meta && meta.copper) || 0);
  const researchN = meta && meta.research ? Object.keys(meta.research).filter((k) => meta.research[k]).length : 0;
  const forgedN = meta && meta.helix && meta.helix.forged ? Object.keys(meta.helix.forged).filter((k) => meta.helix.forged[k]).length : 0;
  const petsN = meta && meta.pets ? Object.keys(meta.pets).filter((k) => meta.pets[k] && meta.pets[k].unlocked).length : 0;
  const shadesN = meta && meta.shades ? meta.shades.length : 0;
  const bagCap = 20 + ((meta && meta.bagExpansions) || 0);
  const mastered = allEarned(profile, character.id);
  const isNew = runs <= 1 && memCollected === 0 && kills === 0;

  // ---- tier progress + the "frontier" (the tier being worked through) ----
  const tierStats = TIER_NAMES.map((name, i) => {
    const tier = i + 1; let total = 0, done = 0;
    for (const cx of CODICES) if (cx.tier === tier) { total++; if (codexComplete(cx.id, collected)) done++; }
    return { name, tier, total, done };
  });
  const frontier = tierStats.find((t) => t.done < t.total) || tierStats[tierStats.length - 1];

  // ---- a quoted fragment of authored lore the player has actually recovered ----
  const collectedMems = Object.keys(collected).filter((id) => collected[id]).map(getMemory).filter(Boolean);
  const quote = collectedMems.length ? rpick(collectedMems).text : null;

  const snapshot = { kills, depth, bosses, memCollected, codexDone, level, runs, at: Date.now() };
  const sections = [];

  // ---------- OPENING ----------
  if (isNew) {
    sections.push({ heading: "A Seeker Descends", lines: [
      rpick([
        `The gates of Relikara open for ${heroName}. Below waits the Vault — a collection of every dangerous wonder the world thought safest to bury.`,
        `${heroName} steps into the dark of Relikara. The Vault has swallowed kingdoms; today it notices one more seeker.`,
        `No one who enters Relikara leaves the same. ${heroName} begins the descent anyway.`,
      ]),
      rpick([
        `A hundred codices sleep unread. Recover the Vault's scattered memories and the Helix of Restoration will remember the world back into being.`,
        `The Helix stands dark, awaiting the memories only a descent can recover. The story of Relikara has yet to be told.`,
        `Every foe you fell, every floor you brave, unearths a fragment of a story older than kingdoms.`,
      ]),
    ] });
  } else {
    sections.push({ heading: "Welcome Back", lines: [
      rpick([
        `${heroName} returns to the Village of Eden — ${notorietyTier(notoriety)}.`,
        `The dark has not forgotten ${heroName}. You are ${notorietyTier(notoriety)}.`,
        `Once more ${heroName} takes up the descent. To the Vault, you are ${notorietyTier(notoriety)}.`,
      ]),
    ] });
  }

  // ---------- SINCE YOU WERE LAST HERE (diff) ----------
  if (prevSnap && !isNew) {
    const parts = [];
    const dk = kills - (prevSnap.kills || 0); if (dk > 0) parts.push(`felled ${dk} more foe${dk === 1 ? "" : "s"}`);
    if (depth > (prevSnap.depth || 0)) parts.push(`pressed to floor ${depth}`);
    const dm = memCollected - (prevSnap.memCollected || 0); if (dm > 0) parts.push(`recovered ${dm} memor${dm === 1 ? "y" : "ies"}`);
    const dc = codexDone - (prevSnap.codexDone || 0); if (dc > 0) parts.push(`restored ${dc} codex line${dc === 1 ? "" : "s"} on the Helix`);
    const db = bosses - (prevSnap.bosses || 0); if (db > 0) parts.push(`broke ${db} guardian${db === 1 ? "" : "s"} of the deep`);
    const dl = level - (prevSnap.level || 0); if (dl > 0) parts.push(`rose ${dl} hero level${dl === 1 ? "" : "s"}`);
    if (parts.length) {
      const joined = parts.length === 1 ? parts[0] : parts.slice(0, -1).join(", ") + " and " + parts[parts.length - 1];
      sections.push({ heading: "Since You Were Last Here", lines: [
        rpick([`You ${joined}.`, `Between then and now, you ${joined}.`, `The chronicle records: you ${joined}.`]),
      ] });
    }
  }

  // ---------- THE STORY SO FAR ----------
  const storyLines = [];
  if (codexDone === 0) {
    storyLines.push(rpick([
      `The Helix stands dark. Not one codex yet shines — the Vault's memory waits, whole and unread, in the deep.`,
      `No codex has been closed. The story of Relikara sleeps, scattered across a hundred halls.`,
    ]));
  } else {
    const pct = Math.round((codexDone / CODEX_COUNT) * 100);
    storyLines.push(rpick([
      `${codexDone} of ${CODEX_COUNT} codices now shine on the Helix — the world is ${pct}% remembered.`,
      `The Helix carries ${codexDone} restored lines (${pct}%). The tale of Relikara takes shape rung by rung.`,
      `You have closed ${codexDone} codices; ${pct}% of the great restoration is done.`,
    ]));
    // where the tale stands, by tier
    const restoredTiers = tierStats.filter((t) => t.done >= t.total && t.total > 0).map((t) => t.name);
    if (restoredTiers.length) {
      storyLines.push(rpick([
        `The age${restoredTiers.length === 1 ? "" : "s"} of ${restoredTiers.join(", ")} ${restoredTiers.length === 1 ? "is" : "are"} whole again.`,
        `${restoredTiers.join(", ")} — remembered in full.`,
      ]));
    }
    if (frontier && frontier.done < frontier.total) {
      storyLines.push(rpick([
        `Now the story presses into ${frontier.name} (${frontier.done}/${frontier.total} restored).`,
        `The frontier of memory is ${frontier.name}: ${frontier.done} of ${frontier.total} lines recovered.`,
      ]));
    }
  }
  if (quote) storyLines.push(`The memories you carry whisper: “${quote.replace(/^./, (c) => c.toUpperCase())}”`);
  sections.push({ heading: "The Story So Far", lines: storyLines });

  // ---------- CURIOSITIES (seeded selection so it varies) ----------
  const pool = [];
  if (kills > 0) pool.push(rpick([`${kills.toLocaleString()} foes have fallen to your hand.`, `You have unmade ${kills.toLocaleString()} of the Vault's horrors.`]));
  if (runs > 1 && kills > 0) pool.push(`Across ${runs} descents, that is ${Math.round(kills / runs)} a run.`);
  if (depth > 0) pool.push(rpick([`Your deepest reach: floor ${depth}.`, `The dark has let you as far as floor ${depth}.`]));
  if (bosses > 0) pool.push(`${bosses} guardian${bosses === 1 ? "" : "s"} of the deep lie broken.`);
  if (memCollected > 0) pool.push(`${memCollected} memories recovered — the Library holds ${TOTAL_MEMORIES}.`);
  if (forgedN > 0) pool.push(`${forgedN} Legendary Memory Relic${forgedN === 1 ? "" : "s"} forged at the Helix.`);
  if (researchN > 0) pool.push(`${researchN} discipline${researchN === 1 ? "" : "s"} of research mastered.`);
  if (petsN > 0) pool.push(`${petsN} companion${petsN === 1 ? "" : "s"} answer your call.`);
  if (shadesN > 0) pool.push(`${shadesN} fallen hero${shadesN === 1 ? "" : "es"} wander as Forgotten in your wake.`);
  if ((meta && meta.bagExpansions) > 0) pool.push(`Your bag has grown to ${bagCap} slots.`);
  if (copper > 0) pool.push(`${copper} copper banked at the forge.`);
  if (insight > 0) pool.push(`${insight} Insight gathered from the deep.`);
  pool.push(`${heroName} stands at hero level ${level}.`);
  if (mastered) pool.push(`You have mastered every feat there is as ${heroName}.`);
  const facts = shuffle(pool).slice(0, Math.min(5, pool.length));

  // ---------- SENDOFF ----------
  const sendoff = rpick(isNew
    ? [`The first door waits. Descend.`, `The Vault holds its breath. Go down.`, `Your story begins in the dark below.`]
    : [`The deep remembers. Descend, and take more of it back.`, `Another descent, another page. Go down.`, `The Helix waits for what you'll carry up. Descend.`]);

  const title = rpick([`The Chronicle of ${heroName}`, `${heroName} — The Story So Far`, `Relikara Remembers ${heroName}`]);
  return { title, heroName, color: (frontier && CODICES.find((c) => c.tierName === frontier.name)?.color) || "#c77dff", sections, facts, sendoff, snapshot };
}
